import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent } from "vue";
const _hoisted_1 = {
  class: "space-y-4 pt-4 tw"
};
const _hoisted_2 = {
  class: "px-4 py-3"
};
const _hoisted_3 = {
  class: "px-4 py-3",
  colspan: "2"
};
const _hoisted_4 = {
  class: "text-center px-4 py-3"
};
const _hoisted_5 = {
  class: "flex items-center justify-center"
};
const _hoisted_6 = {
  class: "text-right px-4 py-3"
};
const _hoisted_7 = {
  class: "flex items-center justify-end"
};
const _hoisted_8 = {
  class: "px-4 py-3"
};
const _hoisted_9 = {
  class: "px-4 py-3",
  colspan: "2"
};
const _hoisted_10 = {
  class: "px-4 py-3"
};
const _hoisted_11 = {
  class: "border-t-2"
};
const _hoisted_12 = {
  class: "text-center px-4 py-3"
};
const _hoisted_13 = {
  key: 0
};
const _hoisted_14 = {
  key: 1
};
const _hoisted_15 = {
  class: "text-right px-4 py-3"
};
const _hoisted_16 = {
  key: 1,
  class: "text-gray-500"
};
const _hoisted_17 = {
  class: "text-right px-4 py-3"
};
const _hoisted_18 = {
  class: "text-right px-4 py-3"
};
import { ref, computed, watch, nextTick } from 'vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlTextField from '@/components/LlTextField.vue';
import { formatCurrency, round2, isAmountEqual, createAllocation, DEFAULT_STEP, PERCENTAGE_SCALE } from '@/services/utils';

// Props

export default {
  __name: 'RevenueAllocationTab',
  props: {
    allocations: {
      type: Array,
      default: () => []
    },
    chargeAmount: {
      type: Number,
      default: 0
    },
    users: {
      type: Array,
      default: () => []
    },
    invoiceAccount: {
      type: Object,
      default: null
    }
  },
  emits: ['update:allocations', 'update:invoiceAccount', 'validation-changed'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;

    // Emits
    const emit = __emit;

    // Local state
    const showPercentage = ref(true);
    const selectedUser = ref(null);

    // Computed properties
    const availableUsers = computed(() => {
      return props.users.filter(user => !props.allocations.some(allocation => allocation.account?.id === user.id));
    });
    const totalAmount = computed(() => {
      return round2(props.allocations.reduce((sum, a) => sum + (Number(a.amount) || 0), 0));
    });
    const totalPercentage = computed(() => {
      if (!props.allocations.find(a => a.percentage)) {
        return 0;
      }
      return round2(props.allocations.reduce((sum, a) => sum + (Number(a.percentage) || 0), 0));
    });
    const isAllocationValid = computed(() => {
      if (props.allocations.length === 0) return true;
      return isAmountEqual(totalAmount.value, props.chargeAmount);
    });

    // Constants for template usage
    const stepValue = computed(() => DEFAULT_STEP);
    const maxPercentage = computed(() => PERCENTAGE_SCALE);

    // Methods
    const togglePercentageView = () => {
      showPercentage.value = !showPercentage.value;
      const updatedAllocations = [...props.allocations];
      if (showPercentage.value) {
        // If we are moving to percentages, set percentages according to amounts
        updatedAllocations.forEach(allocation => {
          allocation.percentage = props.chargeAmount > 0 ? allocation.amount / props.chargeAmount * PERCENTAGE_SCALE : 0;
        });
      } else {
        // If we are moving to hours, clear the percentage numbers (set to undefined)
        updatedAllocations.forEach(allocation => {
          allocation.percentage = undefined;
        });
      }
      emit('update:allocations', updatedAllocations);
    };
    const onPercentageChanged = () => {
      if (!props.chargeAmount || props.chargeAmount <= 0) return;
      const updatedAllocations = [...props.allocations];

      // This mirrors the original AngularJS implementation
      // It doesn't auto-adjust other allocations, just converts percentages to amounts
      const total = showPercentage.value ? PERCENTAGE_SCALE : totalPercentage.value;
      if (total > 0) {
        updatedAllocations.forEach(allocation => {
          // Ensure percentage is valid
          if (allocation.percentage < 0) allocation.percentage = 0;
          if (showPercentage.value && allocation.percentage > PERCENTAGE_SCALE) allocation.percentage = PERCENTAGE_SCALE;
          allocation.amount = Math.round((allocation.percentage || 0) * props.chargeAmount) / total;
        });
      }
      emit('update:allocations', updatedAllocations);
    };

    // Other rows are never rebalanced: the user owns every amount, and the totals
    // validation blocks saving until they add up to the fee.
    const onAllocationAmountChanged = changedAllocation => {
      if (changedAllocation.amount < 0) changedAllocation.amount = 0;
      if (showPercentage.value && props.chargeAmount > 0) {
        changedAllocation.percentage = round2(changedAllocation.amount / props.chargeAmount * PERCENTAGE_SCALE);
      }
      emit('update:allocations', [...props.allocations]);
    };
    const addAllocation = async user => {
      if (!user) return;
      const foundUser = props.users.find(u => u.id === user.id);
      if (foundUser && !props.allocations.find(a => a.account?.id === foundUser.id)) {
        const updatedAllocations = [...props.allocations];
        const newAllocation = createAllocation(foundUser, 0, props.chargeAmount, false);
        updatedAllocations.push(newAllocation);
        emit('update:allocations', updatedAllocations);
        await nextTick();
        selectedUser.value = null;
      }
    };
    const removeAllocation = allocation => {
      if (!allocation.account) return;
      const updatedAllocations = [...props.allocations];
      const index = updatedAllocations.findIndex(a => a.account?.id === allocation.account.id);
      if (index > -1) {
        // Set amount to zero and redistribute before removing
        updatedAllocations[index].amount = 0;

        // Remove the allocation
        updatedAllocations.splice(index, 1);

        // If this was the invoice user, clear it
        if (allocation.main) {
          emit('update:invoiceAccount', null);
        }
      }
      emit('update:allocations', updatedAllocations);
    };
    const setInvoiceUser = allocation => {
      const updatedAllocations = [...props.allocations];

      // Set the main invoice user
      const newInvoiceAccount = allocation.main ? allocation.account : null;

      // Ensure only one allocation is marked as main
      updatedAllocations.forEach(a => {
        a.main = allocation.main && a.account?.id === allocation.account?.id;
      });
      emit('update:allocations', updatedAllocations);
      emit('update:invoiceAccount', newInvoiceAccount);
    };
    const splitEvenly = () => {
      if (props.allocations.length === 0 || !props.chargeAmount || props.chargeAmount <= 0) return;
      const updatedAllocations = [...props.allocations];
      const amount = round2(props.chargeAmount / updatedAllocations.length);
      let remainder = props.chargeAmount;
      updatedAllocations.forEach(allocation => {
        allocation.amount = amount;
        remainder = round2(remainder - amount);
      });

      // Add any remainder to the first allocation
      if (remainder !== 0 && updatedAllocations.length > 0) {
        updatedAllocations[0].amount = round2(updatedAllocations[0].amount + remainder);
      }

      // Update percentages if we're in percentage mode
      if (showPercentage.value) {
        updatedAllocations.forEach(allocation => {
          allocation.percentage = round2(allocation.amount / props.chargeAmount * PERCENTAGE_SCALE);
        });
      }
      emit('update:allocations', updatedAllocations);
    };
    const resetAllocations = () => {
      emit('update:allocations', []);
      emit('update:invoiceAccount', null);
    };

    // Watch for charge amount changes to update percentage calculations
    watch(() => props.chargeAmount, (newAmount, oldAmount) => {
      if (newAmount !== oldAmount && props.allocations.length > 0 && showPercentage.value) {
        const updatedAllocations = [...props.allocations];

        // Recalculate amounts based on existing percentages when charge amount changes
        updatedAllocations.forEach(allocation => {
          if (allocation.percentage && newAmount > 0) {
            allocation.amount = round2(allocation.percentage / PERCENTAGE_SCALE * newAmount);
          }
        });
        emit('update:allocations', updatedAllocations);
      }
    });

    // Watch for validation changes and emit to parent
    watch(isAllocationValid, isValid => {
      emit('validation-changed', isValid);
    }, {
      immediate: true
    });

    // Also emit when allocations or charge amount changes
    watch([() => props.allocations, () => props.chargeAmount], () => {
      emit('validation-changed', isAllocationValid.value);
    }, {
      deep: true
    });
    return (_ctx, _cache) => {
      const _component_v_table = _resolveComponent("v-table");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_cache[25] || (_cache[25] = _createElementVNode("p", {
        class: "text-gray-700"
      }, " This affects the revenue by attorney report. The fixed fee amount can be split between individual users for reporting purposes. ", -1)), _createVNode(_component_v_table, {
        density: "compact",
        class: "mt-4 w-full"
      }, {
        default: _withCtx(() => [_createElementVNode("thead", null, [_createElementVNode("tr", null, [_cache[2] || (_cache[2] = _createElementVNode("th", {
          class: "text-left px-4 py-3"
        }, "Invoice User", -1)), _cache[3] || (_cache[3] = _createElementVNode("th", {
          class: "text-left px-4 py-3",
          colspan: "2"
        }, "User", -1)), _createElementVNode("th", {
          class: "text-center px-4 py-3 cursor-pointer",
          onClick: togglePercentageView
        }, [_createElementVNode("span", {
          class: _normalizeClass(["font-medium", {
            'text-gray-400': !showPercentage.value
          }])
        }, "Percent", 2), _cache[1] || (_cache[1] = _createElementVNode("span", {
          class: "mx-1"
        }, "|", -1)), _createElementVNode("span", {
          class: _normalizeClass(["font-medium", {
            'text-gray-400': showPercentage.value
          }])
        }, "Hours", 2)]), _cache[4] || (_cache[4] = _createElementVNode("th", {
          class: "text-right px-4 py-3"
        }, "Amount", -1)), _cache[5] || (_cache[5] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1))])]), _createElementVNode("tbody", null, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.allocations, (allocation, index) => {
          return _openBlock(), _createElementBlock("tr", {
            key: index
          }, [_createElementVNode("td", _hoisted_2, [_createVNode(LlCheckbox, {
            modelValue: allocation.main,
            "onUpdate:modelValue": $event => allocation.main = $event,
            "aria-label": `Make ${allocation.account?.name ?? 'this user'} the invoice user`,
            onCheckboxClick: $event => setInvoiceUser(allocation)
          }, null, 8, ["modelValue", "onUpdate:modelValue", "aria-label", "onCheckboxClick"])]), _createElementVNode("td", _hoisted_3, _toDisplayString(allocation.account?.name), 1), _createElementVNode("td", _hoisted_4, [_createElementVNode("div", _hoisted_5, [_createVNode(LlTextField, {
            modelValue: allocation.percentage,
            "onUpdate:modelValue": [$event => allocation.percentage = $event, onPercentageChanged],
            modelModifiers: {
              number: true
            },
            type: "number",
            step: stepValue.value,
            min: "0",
            max: showPercentage.value ? maxPercentage.value : undefined,
            suffix: showPercentage.value ? '%' : undefined,
            variant: "outlined",
            density: "compact",
            "hide-details": "",
            class: "w-20"
          }, null, 8, ["modelValue", "onUpdate:modelValue", "step", "max", "suffix"])])]), _createElementVNode("td", _hoisted_6, [_createElementVNode("div", _hoisted_7, [_createVNode(LlTextField, {
            modelValue: allocation.amount,
            "onUpdate:modelValue": [$event => allocation.amount = $event, $event => onAllocationAmountChanged(allocation)],
            modelModifiers: {
              number: true
            },
            type: "number",
            step: stepValue.value,
            min: 0,
            variant: "outlined",
            density: "compact",
            "hide-details": "",
            class: "w-24",
            "data-cy": `allocation-amount-${index}`
          }, null, 8, ["modelValue", "onUpdate:modelValue", "step", "data-cy"])])]), _createElementVNode("td", _hoisted_8, [_createVNode(LlSecondaryButton, {
            icon: "mdi-delete",
            variant: "text",
            color: "error",
            size: "small",
            "aria-label": `Remove ${allocation.account?.name ?? 'user'}`,
            onClick: $event => removeAllocation(allocation)
          }, null, 8, ["aria-label", "onClick"])])]);
        }), 128)), _createElementVNode("tr", null, [_cache[7] || (_cache[7] = _createElementVNode("td", {
          class: "px-4 py-3"
        }, null, -1)), _createElementVNode("td", _hoisted_9, [_createVNode(LlSelect, {
          modelValue: selectedUser.value,
          "onUpdate:modelValue": [_cache[0] || (_cache[0] = $event => selectedUser.value = $event), addAllocation],
          items: availableUsers.value,
          "item-title": "name",
          "item-value": "id",
          placeholder: "Add User",
          variant: "outlined",
          density: "compact",
          "hide-details": "",
          clearable: "",
          class: "min-w-40"
        }, null, 8, ["modelValue", "items"])]), _cache[8] || (_cache[8] = _createElementVNode("td", {
          class: "px-4 py-3"
        }, null, -1)), _createElementVNode("td", _hoisted_10, [__props.allocations.length > 0 ? (_openBlock(), _createBlock(LlSecondaryButton, {
          key: 0,
          variant: "outlined",
          size: "small",
          onClick: splitEvenly
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [_createTextVNode(" Split Evenly ", -1)]))]),
          _: 1
        })) : _createCommentVNode("", true)]), _cache[9] || (_cache[9] = _createElementVNode("td", {
          class: "px-4 py-3"
        }, null, -1))])]), _createElementVNode("tfoot", null, [_createElementVNode("tr", _hoisted_11, [_cache[10] || (_cache[10] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[11] || (_cache[11] = _createElementVNode("th", {
          class: "text-left px-4 py-3"
        }, "Allocated Amount", -1)), _cache[12] || (_cache[12] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _createElementVNode("th", _hoisted_12, [showPercentage.value ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(totalPercentage.value) + "%", 1)) : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(totalPercentage.value), 1))]), _createElementVNode("th", _hoisted_15, [__props.allocations.length ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: _normalizeClass({
            'text-error': totalAmount.value !== __props.chargeAmount
          })
        }, _toDisplayString(_unref(formatCurrency)(totalAmount.value)), 3)) : (_openBlock(), _createElementBlock("span", _hoisted_16, "default"))]), _cache[13] || (_cache[13] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1))]), _createElementVNode("tr", null, [_cache[14] || (_cache[14] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[15] || (_cache[15] = _createElementVNode("th", {
          class: "text-left px-4 py-3"
        }, "Fixed Fee Amount", -1)), _cache[16] || (_cache[16] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[17] || (_cache[17] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _createElementVNode("th", _hoisted_17, _toDisplayString(_unref(formatCurrency)(__props.chargeAmount || 0)), 1), _cache[18] || (_cache[18] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1))]), _createElementVNode("tr", null, [_cache[20] || (_cache[20] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[21] || (_cache[21] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[22] || (_cache[22] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _cache[23] || (_cache[23] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1)), _createElementVNode("th", _hoisted_18, [__props.allocations.length > 0 ? (_openBlock(), _createBlock(LlSecondaryButton, {
          key: 0,
          variant: "outlined",
          size: "small",
          onClick: resetAllocations
        }, {
          default: _withCtx(() => [...(_cache[19] || (_cache[19] = [_createTextVNode(" Reset ", -1)]))]),
          _: 1
        })) : _createCommentVNode("", true)]), _cache[24] || (_cache[24] = _createElementVNode("th", {
          class: "px-4 py-3"
        }, null, -1))])])]),
        _: 1
      })]);
    };
  }
};